import React from 'react'
import { Box, Typography } from '@mui/material'
import { Eye, TrendingUp, Users, Sparkles } from 'lucide-react'
import { formatLongNumber } from 'utils/number'

interface InstagramInsightsProps {
  data:
    | {
        __typename?: 'InstagramInsights' | undefined
        id: number
        startDate: string
        endDate: string
        reach: number
        impressions: number
        profile_views: number
      }
    | null
    | undefined
}

interface InsightCardProps {
  title: string
  value: number
  icon: React.ElementType
  color: string
  tooltip: string
  change?: number // Optional: percentage change from previous period
}

const InsightCard = ({ title, value, icon: Icon, color, tooltip }: InsightCardProps) => {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: '200px',
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(88, 28, 135, 0.2) 100%)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        cursor: 'help',
        '&:hover': {
          transform: 'translateY(-4px)',
          border: '1px solid rgba(168, 85, 247, 0.4)',
          boxShadow: `0 8px 32px ${color}40`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
          opacity: 0.8,
        },
      }}
      title={tooltip}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: `${color}20`,
          mb: 2,
        }}
      >
        <Icon size={24} style={{ color }} />
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.6)',
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {title}
      </Typography>

      {/* Value */}
      <Typography
        sx={{
          fontSize: '32px',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #ffffff 0%, #c084fc 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: 1,
          mb: 1,
        }}
      >
        {formatLongNumber(value)}
      </Typography>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -20,
          right: -20,
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  )
}

export default function InstagramInsights({ data }: InstagramInsightsProps): JSX.Element {
  const insights = [
    {
      title: 'Reach',
      value: data?.reach ?? 0,
      icon: Users,
      color: '#a855f7',
      tooltip: "Total unique users who viewed at least one of your Instagram posts",
    },
    {
      title: 'Impressions',
      value: data?.impressions ?? 0,
      icon: Eye,
      color: '#ec4899',
      tooltip: "Total times your Instagram content has been viewed (includes repeat views)",
    },
    {
      title: 'Profile Views',
      value: data?.profile_views ?? 0,
      icon: TrendingUp,
      color: '#8b5cf6',
      tooltip: "Total users who visited your Instagram profile",
    },
  ]

  // Calculate total engagement
  const totalEngagement = (data?.reach ?? 0) + (data?.impressions ?? 0) + (data?.profile_views ?? 0)

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={20} color="white" />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'rgba(255, 255, 255, 0.9)',
                mb: '2px',
              }}
            >
              Instagram Insights
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              Your recent performance metrics
            </Typography>
          </Box>
        </Box>

        {/* Total Engagement Badge */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Total Engagement
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {formatLongNumber(totalEngagement)}
          </Typography>
        </Box>
      </Box>

      {/* Insights Grid */}
      <Box
        sx={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
        }}
      >
        {insights.map((insight, index) => (
          <InsightCard key={index} {...insight} />
        ))}
      </Box>

      {/* Additional Context (Optional) */}
      {totalEngagement > 0 && (
        <Box
          sx={{
            mt: 3,
            padding: '16px',
            background: 'rgba(30, 41, 59, 0.4)',
            borderRadius: '12px',
            border: '1px solid rgba(168, 85, 247, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.5, transform: 'scale(1.2)' },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '13px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontStyle: 'italic',
            }}
          >
            {data?.impressions && data?.reach
              ? `Average views per unique user: ${(data.impressions / data.reach).toFixed(1)}×`
              : 'Hover over each metric to see detailed descriptions'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}